/**
 * Whether an element is in view, for the "there's a chart below" cue.
 *
 * IntersectionObserver rather than scroll offsets: the chart pane is clipped by the
 * scrolling content area, and the observer already accounts for ancestor clipping.
 */

import { useEffect, useState, type RefObject } from 'react';

interface UseIsInViewOptions {
    /** Skip observing entirely, for when the cue couldn't apply anyway. */
    enabled?: boolean;
    /** How much of the element has to show before it counts as seen. */
    threshold?: number;
}

export function useIsInView(
    ref: RefObject<Element | null>,
    { enabled = true, threshold = 0.15 }: UseIsInViewOptions = {}
): boolean {
    // Assume visible: callers use this to *add* a prompt, and a missing observer should
    // leave the UI alone rather than pin a cue on screen with no way to clear it.
    const [inView, setInView] = useState(true);

    useEffect(() => {
        const element = ref.current;

        if (!enabled || !element || typeof IntersectionObserver === 'undefined') {
            setInView(true);
            return undefined;
        }

        const observer = new IntersectionObserver(
            (entries) => {
                const entry = entries[entries.length - 1];
                if (entry) {
                    setInView(entry.isIntersecting);
                }
            },
            { threshold }
        );

        observer.observe(element);
        return () => {
            observer.disconnect();
        };
    }, [ref, enabled, threshold]);

    return inView;
}
